// src/components/store/OrderProcessing.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  XCircle,
  Loader,
  CreditCard,
  Package,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";
import { useOrders } from "../../hooks/useOrders";
import toast from "react-hot-toast";
import { useSalesContext } from "../../contexts/SalesContext";
import { useSupaContext } from "../../contexts/SupaContext";

const OrderProcessing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderType, orderData } = location.state || {};

  // CAMBIO PRINCIPAL: Usar useRef para evitar problemas de sincronización
  const processingTimeout = useRef(null);

  // Contextos
  const { user } = useSupaContext();
  const { resetData } = useSalesContext();
  const { createOrder } = useOrders();

  // Estados
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [orderResult, setOrderResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(0);

  // Pasos del proceso
  const steps = [
    { id: 1, text: "Validando información", icon: Package },
    { id: 2, text: "Creando tu pedido", icon: Package },
    { id: 3, text: "Preparando todo para el pago", icon: CreditCard },
  ];

  useEffect(() => {
    // Solo ejecutar una vez al montar el componente
    if (!orderData || !user) {
      console.error("❌ OrderProcessing: Faltan datos necesarios", {
        orderData: !!orderData,
        user: !!user,
      });
      navigate("/");
      return;
    }

    const processOrder = async () => {
      console.log("🚀 OrderProcessing: Iniciando proceso de orden", {
        orderType,
        hasOrderData: !!orderData,
        hasUser: !!user,
        timestamp: new Date().toISOString(),
      });

      try {
        // Animación de pasos
        for (let i = 0; i < steps.length; i++) {
          setCurrentStep(i);
          console.log(`📍 Paso ${i + 1} de ${steps.length}: ${steps[i].text}`);
          await new Promise((resolve) => {
            processingTimeout.current = setTimeout(resolve, 800);
          });
        }

        console.log("✅ Animación completada, llamando createOrder...");
        console.log("🔍 Datos antes de createOrder:", {
          orderDataKeys: Object.keys(orderData || {}),
          orderType,
          userId: user?.id,
        });

        // Crear la orden
        const result = await createOrder({ ...orderData, user }, orderType);

        console.log("📦 Resultado de createOrder:", result);

        if (result.success) {
          console.log("✅ Orden creada exitosamente:", result);
          setOrderResult(result);
          setStatus("success");

          // Limpiar datos del contexto
          setTimeout(() => {
            resetData();
          }, 1000);
        } else {
          throw new Error(result.error || "Error al crear la orden");
        }
      } catch (error) {
        console.error("❌ Error procesando orden:", error);
        setErrorMessage(
          error.message || "Ocurrió un error al procesar tu pedido"
        );
        setStatus("error");

        toast.error(error.message || "Hubo un problema al procesar tu pedido", {
          duration: 5000,
          position: "top-center",
          style: {
            marginTop: "60px",
          },
        });
      }
    };

    // Ejecutar solo una vez
    processOrder();

    // Cleanup
    return () => {
      if (processingTimeout.current) {
        clearTimeout(processingTimeout.current);
      }
    };
  }, []); // Array vacío para ejecutar solo al montar

  // Handlers
  const handleGoToPayment = () => {
    if (orderResult?.order?.id) {
      navigate("/pagar", {
        state: {
          orderId: orderResult.order.id,
          orderShortId: orderResult.order.short_id,
          total: orderResult.order.total,
          orderType,
        },
      });
    }
  };

  const handleReturnToCheckout = () => {
    // Volver al paso de resumen según el tipo de orden
    if (orderType === "refill") {
      navigate("/recambios", {
        state: {
          returnToSummary: true,
          previousData: orderData,
          errorMessage,
        },
      });
    } else {
      navigate("/new-items", {
        state: {
          returnToSummary: true,
          previousData: orderData,
          errorMessage,
        },
      });
    }
  };

  // Renderizado condicional según estado
  const renderContent = () => {
    switch (status) {
      case "processing":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Spinner principal */}
            <div className="mb-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Loader className="w-16 h-16 text-blue-600" />
              </motion.div>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4 fraunces">
              Procesando tu pedido
            </h1>

            {/* Pasos del proceso */}
            <div className="space-y-4 max-w-md mx-auto">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;

                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.2 }}
                    className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                      isActive
                        ? "bg-blue-50 border border-blue-200"
                        : isCompleted
                        ? "bg-green-50 border border-green-200"
                        : "bg-gray-50"
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 ${
                        isActive
                          ? "text-blue-600"
                          : isCompleted
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <span
                      className={`text-sm ${
                        isActive
                          ? "text-blue-900 font-medium"
                          : isCompleted
                          ? "text-green-900"
                          : "text-gray-500"
                      }`}
                    >
                      {step.text}
                    </span>
                    {isActive && (
                      <motion.div
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="ml-auto"
                      >
                        <div className="w-2 h-2 bg-blue-600 rounded-full" />
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            <p className="text-gray-500 mt-6 text-sm">
              Esto puede tomar unos segundos...
            </p>
          </motion.div>
        );

      case "success":
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="text-center"
          >
            {/* Icono de éxito animado */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="mb-6"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </motion.div>

            <h1 className="text-4xl fraunces font-bold text-gray-900 mb-2">
              ¡Pedido creado exitosamente!
            </h1>

            <p className="text-gray-600 mb-8">
              Tu pedido #{orderResult?.order?.short_id || "XXXXX"} ha sido
              creado correctamente
            </p>

            {/* Detalles del pedido */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Número de orden:</span>
                  <span className="font-semibold text-gray-900">
                    #{orderResult?.order?.short_id}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Items:</span>
                  <span className="font-semibold text-gray-900">
                    {orderResult?.summary?.itemCount || 0}
                  </span>
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-medium">
                      Total a pagar:
                    </span>
                    <span className="font-bold text-xl text-gray-900">
                      ${orderResult?.order?.total?.toLocaleString("es-CO") || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Botón de pago */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGoToPayment}
              className="w-full max-w-md mx-auto bg-blue-600 text-white font-medium py-4 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Continuar al pago
            </motion.button>

            <p className="text-sm text-gray-500 mt-4">
              Serás redirigido a nuestra plataforma de pago seguro
            </p>
          </motion.div>
        );

      case "error":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Icono de error */}
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Ocurrió un problema
            </h1>

            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {errorMessage || "No pudimos procesar tu pedido en este momento"}
            </p>

            {/* Mensaje de ayuda */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm text-amber-800">
                    No te preocupes, no se ha realizado ningún cargo. Puedes
                    intentar nuevamente o contactar a soporte si el problema
                    persiste.
                  </p>
                </div>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="space-y-3 max-w-md mx-auto">
              <button
                onClick={handleReturnToCheckout}
                className="w-full bg-blue-600 text-white font-medium py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Volver al resumen
              </button>

              <button
                onClick={() => navigate("/app")}
                className="w-full bg-white text-gray-700 font-medium py-3 px-6 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Ir al inicio
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
      </div>
    </div>
  );
};

export default OrderProcessing;
